import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { StrictMode, useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as api from './api.ts';
import { session } from './api.ts';
import { NoActiveOrg, SignIn } from './screens.tsx';
import { Activate, Live } from './screens/events.tsx';
import { Activity } from './screens/activity.tsx';
import { Venue } from './screens/venue.tsx';
import { People } from './screens/people.tsx';
import { Teams } from './screens/teams.tsx';
import { Locations } from './screens/locations.tsx';
import { BuildingWizard } from './screens/building-wizard.tsx';
import { Home } from './screens/home.tsx';
import { Setup } from './screens/setup.tsx';
import { Capture } from './screens/capture.tsx';
import { Overview } from './screens/readiness.tsx';
import { Hardware } from './screens/hardware.tsx';
import { Settings } from './screens/settings.tsx';
import { Evidence } from './screens/evidence.tsx';
import { Cases } from './screens/cases.tsx';
import { Disclosure } from './screens/disclosure.tsx';
import { Redaction } from './screens/redaction.tsx';
import { Shell, type Screen } from './shell.tsx';
import { Spinner } from './ui.tsx';
import { useDisclosure } from './lib/disclosure.ts';
import { ErrorBoundary } from './error-boundary.tsx';
import { StaleBuildNotice } from './stale-build-notice.tsx';
import { WorkMobileBanner, shouldShowWorkMobileBanner } from './work-mobile.tsx';
import { InvitationPage, parseInvitationPath } from './invitation.tsx';
import { JoinPage, parseJoinPath } from './join-link.tsx';
import { installStaleBuildRecovery } from './lib/stale-build.ts';
import './styles.css';

/**
 * Routing is the URL and nothing more.
 *
 * A dashboard that loses its place on refresh is a dashboard that loses its
 * place mid-incident, so the live event view has a real address someone can
 * reload or send to a colleague. That is the whole requirement, and it does not
 * justify a router.
 */

export interface Route {
  name: Screen | 'event';
  orgId?: string;
  eventId?: string;
  /**
   * The selected building, when the route is a single building's page
   * (`/locations/<siteId>`). A building detail is a real address so back,
   * forward and reload land on the building an admin was looking at rather than
   * bouncing to the buildings list (buildings became a full page, not a
   * slide-over — `design-dashboard.md` §11's building carve-out, Trav
   * 2026-09-13).
   */
  siteId?: string;
}

export function parse(pathname: string): Route {
  const event = /^\/orgs\/([0-9a-f-]{36})\/events\/([0-9a-f-]{36})$/.exec(pathname);
  if (event) return { name: 'event', orgId: event[1], eventId: event[2] };

  // Adding a building: the full-screen building setup wizard at its own route
  // (piece 2). It sits above `/locations/<siteId>` deliberately — "new" is not a
  // 36-char UUID so it can never be mistaken for a building id — and resolves to
  // its own screen rather than the locations list. The wizard lands the operator
  // on `/locations/<newSiteId>` when it finishes.
  if (pathname === '/locations/new') return { name: 'building-new' };

  // A single building's page: `/locations/<siteId>`. It resolves to the same
  // `locations` screen (one render branch, `screen-drift.test.ts`) carrying the
  // building it opens on, so a reload or a shared link reopens that building.
  const building = /^\/locations\/([0-9a-f-]{36})$/.exec(pathname);
  if (building) return { name: 'locations', siteId: building[1] };

  // A navigation may carry a sub-target as a URL hash (e.g.
  // `/settings#golive-drillCompleted` — the banner's deep link to the next
  // going-live step). The hash steers the destination screen's own initial
  // state; it is not part of the screen id, so strip it (and any query) before
  // resolving the screen. `window.location.pathname` never carries either, so
  // this only bites the `go(path)` call that passes the full string.
  const path = pathname.replace(/^\//, '').replace(/[?#].*$/, '');

  /**
   * The screens that no longer exist, and where their content went.
   *
   * Kept rather than dropped: these are URLs in somebody's bookmarks and in
   * the browser history of every admin who has used this. A 404 or a silent
   * bounce to Home would be the wrong answer to "I had that page open".
   */
  const moved: Record<string, Screen> = {
    // The full readiness report is now its own screen rather than a slab on
    // Home, so the old /overview bookmark lands on it.
    overview: 'readiness',
    // The org's buildings screen is now the `locations` rail directory (owner IA
    // 2026-09-07). Its content lived under /sites, then /places (the internal id
    // when it sat under Settings); both are in bookmarks, and both resolve to
    // it. "Places" is reserved for the consumer app, so the org route is named
    // `locations`.
    sites: 'locations',
    places: 'locations',
    // The ECO / "who sweeps where" board moved onto Locations with the buildings
    // it belongs to (owner IA), so the old /eco bookmark lands there.
    eco: 'locations',
    presence: 'home',
    venue: 'settings',
    reports: 'activity',
    events: 'activity',
    // Devices was renamed Hardware (2026-09-05) so the rail item no longer
    // collides with the Duress/personal-safety response family; the old URL is
    // in bookmarks and resolves to the screen its content stayed on.
    devices: 'hardware',
    // 'History' was renamed 'Activity' at the First Light freeze (2026-08-31);
    // the old URL stays in bookmarks, so it resolves like every retired URL.
    history: 'activity',
  };
  if (moved[path]) {
    const name = moved[path]!;
    // Heal the address bar: a retired path (/sites, /eco, /reports…) still
    // resolves, but rewrite the URL to the canonical /locations, /activity, …
    // so a bookmark or copy taken from here captures the real one. Only when the
    // URL actually differs, and with replaceState (not push) so no history entry
    // is added and back/forward are untouched.
    const canonical = `/${name}`;
    if (typeof window !== 'undefined' && window.location.pathname !== canonical) {
      window.history.replaceState({}, '', canonical);
    }
    return { name };
  }

  const screen = path as Screen;
  const known: Screen[] = [
    'home',
    'people',
    'teams',
    'locations',
    'hardware',
    'settings',
    // Off the rail but still real destinations (Home links, Settings hub,
    // bookmarks).
    'readiness',
    'setup',
    'activity',
    'evidence',
    'cases',
    'disclosure',
    'redaction',
    'capture',
    'activate',
  ];
  return { name: known.includes(screen) ? screen : 'home' };
}

/**
 * The app-wide selected building implied by the current route, or `null` for
 * "leave the current focus alone".
 *
 * MEDIUM (board, PR #448): a `/locations/<siteId>` URL is user-supplied and may
 * name a building this account does not own — a stale bookmark, or a link shared
 * from another org. That id must NEVER become the app-wide Raise/Activate
 * target, so it is honoured only when it is a real, owned building (present in
 * `siteList`). The bare list (`/locations`, no id) clears the focus, so a browser
 * Back to the list stops the raise chrome from targeting the building just left
 * (it falls back to `siteList[0]`). Any other route leaves the focus untouched.
 *
 * Pure and idempotent: the effect that calls it never changes the route and
 * setting the same value is a React no-op, so there is no render loop.
 */
export function routeSiteFocus(
  routeName: Route['name'],
  routeSiteId: string | undefined,
  siteList: { id: string }[],
): string | null {
  if (routeName !== 'locations') return null;
  if (!routeSiteId) return '';
  return siteList.some((s) => s.id === routeSiteId) ? routeSiteId : null;
}

/**
 * The per-org "I have left setup" flag. An admin whose org has zero sites is
 * redirected into guided setup by the first-run gate; without a way to record
 * that they chose to leave, the gate would bounce them straight back every time
 * they reach Home. Skip and Done write this; the gate reads it. Wrapped in
 * try/catch like every other `localStorage` touch — a private window that
 * refuses storage must still let the app run.
 */
const SETUP_DISMISSED_PREFIX = 'evac.setup-dismissed:';

function setupDismissed(orgId: string): boolean {
  if (!orgId) return false;
  try {
    return localStorage.getItem(`${SETUP_DISMISSED_PREFIX}${orgId}`) === 'true';
  } catch {
    return false;
  }
}

function dismissSetup(orgId: string): void {
  if (!orgId) return;
  try {
    localStorage.setItem(`${SETUP_DISMISSED_PREFIX}${orgId}`, 'true');
  } catch {
    // The redirect will re-fire next time, which is a mild annoyance, not a
    // trap — the rail still works and the wizard still offers its own way out.
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // An operator refreshing during an incident wants the truth, not a cached
      // count from thirty seconds ago.
      staleTime: 0,
      // Never a 4xx — retrying a 401 turns one dead session into a wall of
      // requests that reads as an outage. One retry for network/5xx.
      retry: api.shouldRetry,
      refetchOnWindowFocus: true,
    },
  },
});

function Dashboard({ onSignedOut }: { onSignedOut: () => void }) {
  const [route, setRoute] = useState<Route>(() => parse(window.location.pathname));
  const [siteId, setSiteId] = useState('');

  /**
   * A one-shot site filter for the People screen — set when a building page's
   * "Alert audience" drill-down sends the operator to the named list (People,
   * filtered to that building) rather than duplicating a roster table. It is
   * consumed by People as its initial site filter on mount, then cleared when
   * the operator leaves People, so a later rail visit opens People unfiltered.
   */
  const [pendingPeopleSite, setPendingPeopleSite] = useState('');

  useEffect(() => {
    const onPop = () => setRoute(parse(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Drop the pending People filter the moment the operator navigates off People,
  // so it is genuinely one-shot: only a fresh drill-down sets it again.
  useEffect(() => {
    if (route.name !== 'people') setPendingPeopleSite('');
  }, [route.name]);

  const go = useCallback((path: string) => {
    window.history.pushState({}, '', path);
    setRoute(parse(path));
  }, []);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.getMe,
    // While the work-mobile banner is up, re-ask every 15s so it steps aside
    // when an event starts in one of their orgs (the server's `promptNow`
    // goes false). It never blocks anything, so this is tidiness, not safety.
    refetchInterval: (q) => (q.state.data?.workMobile?.promptNow ? 15_000 : false),
  });
  const memberships = useMemo(() => me.data?.memberships ?? [], [me.data]);

  /**
   * Which org the dashboard is currently pointed at.
   *
   * Trav (and a handful of others) are members of more than one — DOJ NSW
   * rehearsal, DOJ NSW Parramatta, Duress test — and until 2026-08-30 the
   * dashboard silently picked `memberships[0]` and never exposed the rest.
   * A stuck event under any other org was invisible.
   *
   * The stored id survives a reload but is *validated* against the current
   * memberships list on every load: an org this account was removed from
   * cannot become the org the whole dashboard is scoped to.
   */
  const [storedActiveOrgId, setStoredActiveOrgId] = useState<string>(() => {
    try {
      return localStorage.getItem('evac.active-org') ?? '';
    } catch {
      return '';
    }
  });
  const activeOrgId =
    memberships.find((m) => m.org_id === storedActiveOrgId)?.org_id ?? memberships[0]?.org_id ?? '';
  const orgId = activeOrgId;
  const orgName = memberships.find((m) => m.org_id === activeOrgId)?.org_name ?? '';

  const qc = useQueryClient();
  const switchOrg = useCallback(
    (nextOrgId: string) => {
      if (nextOrgId === activeOrgId) return;
      try {
        localStorage.setItem('evac.active-org', nextOrgId);
      } catch {
        // The choice still applies for this session; it just will not be
        // remembered next time.
      }
      setStoredActiveOrgId(nextOrgId);
      // Site chosen under the previous org does not exist under the new one.
      setSiteId('');
      // Everything else refetches on its own — every scoped query key carries
      // `orgId`, so switching the id switches the answers.
      void qc.invalidateQueries();
    },
    [activeOrgId, qc],
  );

  // The evidence entitlement gates a rail item, the Settings workspace links,
  // and the evidence-cluster routes — read once here and shared (react-query
  // dedupes the ['entitlements', orgId] key with Settings' own read).
  const entitlements = useQuery({
    queryKey: ['entitlements', orgId],
    queryFn: () => api.getEntitlements(orgId),
    enabled: Boolean(orgId),
  });
  const evidenceEnabled = entitlements.data?.evidence === true;

  const sites = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => api.getSites(orgId),
    enabled: Boolean(orgId),
  });

  const siteList = sites.data?.sites ?? [];
  const currentSite = siteId || siteList[0]?.id || '';

  // A building page (`/locations/<siteId>`) reached by reload, back/forward or a
  // shared link carries the building in the URL, not in app state — so sync the
  // app-wide selected site (what the chrome's raise entry and the Activate screen
  // target) to it. But ONLY to a building this account actually owns: a stale or
  // cross-org id must not become the Raise target (MEDIUM, PR #448). `siteList`
  // is a dependency so the check re-runs once sites load. The bare list clears
  // the focus. See `routeSiteFocus` for the full rationale — it is pure and
  // idempotent, so this effect never loops and never changes the route.
  useEffect(() => {
    const next = routeSiteFocus(route.name, route.siteId, siteList);
    if (next !== null) setSiteId(next);
  }, [route.name, route.siteId, siteList]);

  // Progressive-disclosure gates for enterprise-oriented surfaces
  // (`docs/progressive-disclosure.md`). Phase 1: every gate answers `true`,
  // so no screen changes; the wiring lives at the render site rather than
  // inside the component so Phase 2 can turn a surface off without touching
  // the component that draws it. `currentSite` is passed so per-site
  // triggers (e.g. `second-zone`) will land here in Phase 2 without a
  // signature change.
  const disclosure = useDisclosure(orgId || undefined, currentSite || undefined);

  /**
   * First-run gate. An admin whose organisation has no sites has nothing to
   * drill and nothing to show on Home, so they land in guided setup rather than
   * on an empty map. It is a redirect from the default screen only — never a
   * lock — so the rail still works and they can leave setup at any point (the
   * wizard also offers its own way out). Non-admins with no sites are not sent
   * there: they cannot create one, so it would be a dead end.
   *
   * It waits for the sites query to actually succeed; redirecting on a loading
   * or failed fetch would bounce an admin whose building exists into setup for
   * a heartbeat.
   */
  const currentRole = memberships.find((m) => m.org_id === orgId)?.role;
  const needsSetup =
    Boolean(orgId) &&
    sites.isSuccess &&
    siteList.length === 0 &&
    currentRole !== undefined &&
    api.isAdminRole(currentRole);

  useEffect(() => {
    if (!needsSetup || route.name !== 'home') return;
    // The escape hatch. Skip/Done in the wizard set a per-org "dismissed" flag;
    // without honouring it here the gate re-fires `go('/setup')` the instant the
    // wizard lands the admin back on Home, and the way out bounces straight back
    // in. Read live from localStorage (not memoised) so the flag the wizard just
    // wrote is seen on this same render pass. Creating a site clears the gate on
    // its own — `siteList` is then non-empty and `needsSetup` is false.
    if (setupDismissed(orgId)) return;
    go('/setup');
  }, [needsSetup, route.name, go, orgId]);

  if (me.isLoading) return <Spinner />;

  if (!orgId) {
    // A signed-in account that holds no membership: they have just verified an
    // OTP for an address that has never held an org. Send them straight into
    // org-creation rather than dead-ending at the shell — that empty screen
    // was the whole reason nobody could walk through onboarding from a fresh
    // signup. On success `me` refetches, memberships is non-empty, and this
    // branch stops matching on the next render.
    //
    // But only when `me` actually loaded: a failed fetch also leaves
    // memberships empty, and must render as a failure with Retry — never as
    // "Name the organisation" (`NoActiveOrg`).
    return <NoActiveOrg me={me} onSignOut={onSignedOut} />;
  }

  // The live view is full-bleed and dark, so it renders outside the shell.
  //
  // NOTE (map persistence scope): the "one persistent map that survives
  // navigation" guarantee (map-layer.tsx) is scoped to the *shell* screens only.
  // Live is its own full-bleed map rendered outside the shell, so Home→Live→Home
  // intentionally tears the shell map down and rebuilds it — that is by design,
  // not a persistence regression.
  if (route.name === 'event' && route.orgId && route.eventId) {
    // Closing the live command view returns to Home — the map and the running
    // event — not the readiness report. (/overview now resolves to the
    // readiness slab, so the old target dumped the operator there.)
    return <Live orgId={route.orgId} eventId={route.eventId} onBack={() => go('/home')} />;
  }

  const openLive = (eventId: string) => {
    go(`/orgs/${orgId}/events/${eventId}`);
  };

  const rawScreen = route.name as Screen;

  /**
   * The evidence workspace routes gate on the entitlement. An org that is
   * positively not entitled (or whose plan read failed) falls back to Home
   * rather than rendering an evidence shell that then 403s the seam — so a
   * bookmark or a stale link degrades gracefully. While the plan is still
   * loading the route is held and a spinner shows, so an entitled deep-link
   * never flashes Home.
   */
  const EVIDENCE_ROUTES: Screen[] = ['evidence', 'cases', 'disclosure', 'redaction', 'capture'];
  const wantsEvidence = EVIDENCE_ROUTES.includes(rawScreen);
  const evidenceResolved = entitlements.isSuccess || entitlements.isError;
  const evidenceLoading = wantsEvidence && !evidenceResolved;
  const screen: Screen = wantsEvidence && evidenceResolved && !evidenceEnabled ? 'home' : rawScreen;

  return (
    <Shell
      screen={screen}
      onNavigate={(s, hash) => go(`/${s}${hash ?? ''}`)}
      orgId={orgId}
      /* `bleed`: the screen manages its own edges over the exposed map — Home,
         the building wizard and the raise panel (a panel slides from the rail
         over the left half while the substrate map, fed by the screen, owns
         the right). */
      bleed={screen === 'home' || screen === 'building-new' || screen === 'activate'}
      orgName={orgName}
      memberships={memberships}
      onSwitchOrg={switchOrg}
      onSignOut={onSignedOut}
      evidenceEnabled={evidenceEnabled}
      /* The org's buildings are the persistent map's default content; a building
         selection (via a Home pin tap) syncs the app-wide site so a raise opens
         on it (H2). There is no longer a rail site switcher — filtering is
         per-screen and the map defaults to a sensible building on its own. */
      sites={siteList}
      onFocusSite={setSiteId}
    >
      {/*
        One boundary around the screen area: a crash in any single screen shows
        a recoverable card while the shell's navigation stays put, so an operator
        is never stranded on a white page mid-incident. Keyed by `screen` so
        moving to another screen clears a failed one.
      */}
      <ErrorBoundary resetKey={screen}>
        {/* The work-mobile requirement (0138): a banner, never a wall — it
            covers nothing and blocks nothing, and the live view (rendered
            outside the shell) never shows it. */}
        {/* Not on the full-bleed screens: there the content area passes clicks
            through to the map and fixed controls sit over the top edge, so the
            banner was covered and unclickable (Trav, 2026-09-30). Home carries
            the ask as a card in its notification strip instead. */}
        {shouldShowWorkMobileBanner(me.data, route.name) &&
        screen !== 'home' &&
        screen !== 'building-new' ? (
          <WorkMobileBanner me={me.data!} />
        ) : null}
        {/*
        An evidence route reached before the plan has resolved: hold with a
        spinner rather than flashing Home, so an entitled deep-link lands on the
        workspace and only a genuinely un-entitled org falls back to Home.
      */}
        {evidenceLoading ? <Spinner /> : null}
        {/*
        Home answers "what is happening", so the readiness picture and who is
        actually in the building sit together. Presence was its own entry in a
        list of ten, which made "who is on site" a place you navigate to rather
        than the first thing you see.
      */}
        {screen === 'home' ? (
          <Home
            orgId={orgId}
            onOpen={openLive}
            /* A to-do row fixes a gap on a specific building, so select that
               building before navigating to the screen that fixes it. */
            onNavigate={(s, sid) => {
              if (sid) setSiteId(sid);
              go(`/${s}`);
            }}
            /* Only an admin can create the first building, so only an admin is
               offered the empty-org setup CTA — the same role check the
               first-run gate above uses. */
            canSetUp={currentRole !== undefined && api.isAdminRole(currentRole)}
            workMobileAsk={
              shouldShowWorkMobileBanner(me.data, route.name)
                ? { onOpen: () => go('/settings#you') }
                : undefined
            }
          />
        ) : null}

        {/*
        The full readiness report — every building, every gap, the alert
        audience, area, assembly-point and ECO numbers, the last-drill date. It used to
        render as a slab on Home covering the map; Home now carries only the
        shortlist and this is where the detail lives (`design-dashboard.md` §8).
      */}
        {screen === 'readiness' ? (
          <Overview
            orgId={orgId}
            sites={siteList}
            /* A gap is fixed on a specific building, so select it before
               navigating to the screen that fixes it — the same contract Home's
               to-do rows used before the readiness screen became their home. */
            onNavigate={(s, sid) => {
              if (sid) setSiteId(sid);
              go(`/${s}`);
            }}
          />
        ) : null}

        {/*
        Guided setup — the first-run flow (`docs/progressive-disclosure.md`
        Phase 3). Reached by the first-run gate above for an admin with no
        sites, and by the /setup URL any time an admin wants to walk it again.
        It creates real orgs → sites → zones → people, so re-entering detects
        what exists rather than starting over. It sits inside the same screen-area
        ErrorBoundary as every other screen, so a throw in setup is caught.
      */}
        {screen === 'setup' ? (
          <Setup
            orgId={orgId}
            orgName={orgName}
            onDone={() => {
              // Record the dismissal before navigating, so the first-run gate
              // sees the flag on the render that go('/home') triggers and does
              // not bounce the admin back into setup.
              dismissSetup(orgId);
              go('/home');
            }}
          />
        ) : null}

        {/*
        The full-screen building setup wizard (piece 2), reached at
        /locations/new from the Locations "Add building" CTA. It creates the
        building at step 1, then walks assembly points, areas, wardens and
        people — every step after the first genuinely skippable — and lands the
        operator on the new building's full page (`/locations/<id>`) when it
        finishes or they skip to the end. Cancel (before a building exists)
        returns to the buildings list.
      */}
        {screen === 'building-new' ? (
          <BuildingWizard
            orgId={orgId}
            onComplete={(newSiteId) => {
              // Keep the app-wide focus in step (the raise chrome and Activate
              // read it) and land on the building they just built.
              setSiteId(newSiteId);
              go(`/locations/${newSiteId}`);
            }}
            onExit={() => go('/locations')}
          />
        ) : null}

        {/*
        Locations — the org's BUILDINGS, their areas, assembly points, the ECO /
        "who sweeps where" board, and the equipment register. Its own rail
        directory (owner IA 2026-09-07): list of buildings → building detail. The
        route id is `locations` — "Places" is reserved for the consumer app, so
        the org route never carries it (the `places`/`sites` bookmarks redirect
        here). The physical equipment lives here because an AED or a first-aid
        kit belongs to a building, not to the Duress-hardware ecosystem on
        Hardware.
      */}
        {screen === 'locations' ? (
          <Locations
            orgId={orgId}
            sites={siteList}
            /* So a reload of `/locations/<id>` holds a loading affordance rather
               than flashing the directory before the building resolves, and a
               genuinely absent id only shows not-found once sites have loaded. */
            sitesLoading={sites.isPending}
            /* The selected building comes from the URL now, not app state: a
               building is a real route (`/locations/<siteId>`) so back, forward
               and reload land on it. An empty selection (`/locations`) shows the
               LIST; selecting a building pushes its route, and "Back to all
               buildings" pops to `/locations`. */
            siteId={route.name === 'locations' ? (route.siteId ?? '') : ''}
            onSite={(id) => {
              // Keep the app-wide focus in step (the raise chrome and Activate
              // read it) and make the selection a real navigation.
              setSiteId(id);
              go(id ? `/locations/${id}` : '/locations');
            }}
            /* "Review" a building's gaps opens the readiness report, where the
               gaps are listed — not Home, which now shows only the shortlist. */
            onReview={() => go('/readiness')}
            /* Zero buildings drives into the guided wizard rather than a bare
               add form — the "zero locations → the wizard" rule (Trav's IA
               call). */
            onSetup={() => go('/setup')}
            /* "Add building" opens the full-screen building setup wizard at its
               own route (piece 2), not a slide-over drawer — a whole building is
               a page, the same call the building detail made. */
            onAddBuilding={() => go('/locations/new')}
            /* The summary header's "Raise / run a drill" reuses the chrome's
               existing raise entry — the Activate screen, which targets the
               app-wide selected site. The building page is open for exactly that
               site (siteId is the selection), so navigating is enough; no new
               endpoint. */
            onRaise={() => go('/activate')}
            /* The building page's "Alert audience" drill-down: open People
               filtered to this building rather than duplicating a roster table.
               Set the one-shot filter, then navigate. */
            onPeople={(sid) => {
              setPendingPeopleSite(sid);
              go('/people');
            }}
          />
        ) : null}

        {/*
        Hardware — "what you have": Duress hardware, vehicles and integrations.
        An honest connect surface with no backing model yet (guardrail G20), so
        it invents no device or vehicle rows — a real nav home for the
        ecosystem, populated when the integration ships. Renamed from "Devices"
        so the rail item does not read as the Duress/personal-safety response
        family.
      */}
        {screen === 'hardware' ? <Hardware /> : null}

        {/*
        People — a pure directory of people. Who they are and how each can be
        reached, filterable by role/team/site, with the person detail drawer. A
        person's team membership shows and can be quick-assigned from the drawer,
        but the Teams *entity* is managed on the Teams screen and the ECO / "who
        sweeps where" board moved onto Locations with the buildings it belongs
        to (owner IA 2026-09-07) — one entity per rail directory.
      */}
        {screen === 'people' ? <People orgId={orgId} initialSiteId={pendingPeopleSite} /> : null}

        {/*
        Teams — the org-scoped groupings that organise and filter people
        (docs/teams.md): a list of teams → team detail (members, sites, a
        designated manager, rename/delete, and team settings). Its own rail
        directory because a team is its own entity, not a section of People.
      */}
        {screen === 'teams' ? <Teams orgId={orgId} /> : null}

        {screen === 'activate' ? (
          <Activate
            orgId={orgId}
            sites={siteList}
            siteId={currentSite}
            onSite={setSiteId}
            onLive={openLive}
            onClose={() => go('/home')}
            onOpenBuilding={(id) => go(`/locations/${id}`)}
          />
        ) : null}

        {/*
        One record of what happened. A report is an artifact of a closed event
        rather than a separate kind of thing, and the audit trail is evidence
        rather than configuration — it had no business living in Settings.
      */}
        {screen === 'activity' ? (
          <Activity
            orgId={orgId}
            role={memberships.find((m) => m.org_id === orgId)?.role ?? 'occupant'}
            onOpenLive={openLive}
            evidenceEnabled={evidenceEnabled}
            onOpenEvidence={() => go('/evidence')}
          />
        ) : null}

        {/*
        The record this organisation may one day be asked to produce. The
        module carries its own gates — seam-unconfigured, org onboarding, and
        the reason-for-access prompt — so it renders unconditionally and
        answers with the right sentence for the state it finds.
      */}
        {screen === 'evidence' && evidenceEnabled ? (
          <Evidence
            orgId={orgId}
            role={memberships.find((m) => m.org_id === orgId)?.role ?? 'occupant'}
            onOpenCapture={() => go('/capture')}
            onOpenReports={() => go('/activity')}
          />
        ) : null}

        {/*
        The evidence workspace surfaces — cases, disclosure and redaction —
        sit on the same token seam as Evidence and carry the same three seam
        states (unconfigured, not-enabled, reason-for-access). `onGoToEvidence`
        sends a not-enabled org to the one place the module is switched on.
      */}
        {screen === 'cases' && evidenceEnabled ? (
          <Cases
            orgId={orgId}
            role={memberships.find((m) => m.org_id === orgId)?.role ?? 'occupant'}
            onGoToEvidence={() => go('/evidence')}
          />
        ) : null}

        {screen === 'disclosure' && evidenceEnabled ? (
          <Disclosure
            orgId={orgId}
            role={memberships.find((m) => m.org_id === orgId)?.role ?? 'occupant'}
            onGoToEvidence={() => go('/evidence')}
          />
        ) : null}

        {screen === 'redaction' && evidenceEnabled ? (
          <Redaction
            orgId={orgId}
            role={memberships.find((m) => m.org_id === orgId)?.role ?? 'occupant'}
            onGoToEvidence={() => go('/evidence')}
          />
        ) : null}

        {/*
        Browser capture: record an incident on a phone and ingest it as
        evidence, without hardware. It is graded `declared`, not `captured` —
        the honest grade for an unsigned browser recording (see
        `lib/capture.ts`). The custodian is the signed-in user.
      */}
        {screen === 'capture' && evidenceEnabled ? (
          <Capture
            orgId={orgId}
            custodian={me.data?.userId ?? 'unknown'}
            onOpenEvidence={() => go('/evidence')}
          />
        ) : null}

        {screen === 'settings' ? (
          <>
            <Settings orgId={orgId} orgName={orgName} onNavigate={(s) => go(`/${s}`)} />
            {/*
            Linking a site to the venue it sits inside is account setup, not
            something anybody does during an incident. The other half of Venue
            — an escalation the centre has to acknowledge — is live traffic and
            belongs with Events; that move is named in §8 and not made here.
          */}
            {/*
            The Venue screen appears only once a `venue_tenancies` handshake
            is created and confirmed between this org and a premises operator
            (`docs/progressive-disclosure.md` §"7. Multi-tenant venue tenancy").
            Phase 1 always renders it; Phase 2 hides it for the many
            single-site occupiers that will never sit inside a Westfield.
          */}
            {disclosure.isVisible('venueScreen') ? (
              <div className="mt-10 border-t border-[var(--line)] pt-8">
                <Venue orgId={orgId} sites={siteList} siteId={currentSite} onSite={setSiteId} />
              </div>
            ) : null}
          </>
        ) : null}
      </ErrorBoundary>
    </Shell>
  );
}

/** One sentence for every way the SSO redirect can end badly — the server's
 *  failure page is deliberately undifferentiated (an oracle otherwise), so
 *  the client's is too. */
const SSO_FAILED = 'Sign-in with Microsoft did not complete. Try again, or use an email code.';

/** A first Microsoft sign-in whose address matches an existing account waits
 *  on the mailbox link — the proof the IdP's asserted claim is not. */
const SSO_PENDING =
  'Almost there — open the link we just emailed to the address on your account to finish connecting your Microsoft sign-in.';

/** Remove the SSO handoff artifacts from the address bar. The code is
 *  single-use and the notices are shown once; none belongs in history, a
 *  bookmark, or the return path a later sign-in restores. */
function stripSsoParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('sso');
  url.searchParams.delete('sso_error');
  url.searchParams.delete('sso_pending');
  const query = url.searchParams.toString();
  window.history.replaceState({}, '', url.pathname + (query ? `?${query}` : '') + url.hash);
}

function App() {
  const [signedIn, setSignedIn] = useState(session.signedIn);
  const [ssoError, setSsoError] = useState<string | null>(null);

  /**
   * The tail of the SSO redirect dance: the API's callback lands the browser
   * back here with a single-use handoff code in the URL, and this exchanges
   * it for the session the OTP flow would have minted. Runs once on mount —
   * the code works once, so re-running it can only produce a false failure.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sso');
    const failed = params.get('sso_error');
    const pending = params.get('sso_pending');
    if (failed) {
      stripSsoParams();
      setSsoError(SSO_FAILED);
      return;
    }
    if (pending) {
      stripSsoParams();
      setSsoError(SSO_PENDING);
      return;
    }
    if (!code) return;
    void api
      .redeemSso(code)
      .then(() => {
        stripSsoParams();
        setSignedIn(true);
        void queryClient.invalidateQueries();
      })
      .catch(() => {
        stripSsoParams();
        setSsoError(SSO_FAILED);
      });
  }, []);

  /**
   * The session dying is a fact the API client discovers (a refresh the
   * server refused, a 401 no refresh can cure), not something a screen asks
   * about — so it arrives as a signal. Route to sign-in *without touching the
   * URL*: the address bar still holds the live event view, so a fresh sign-in
   * re-parses `window.location.pathname` and lands the operator straight back
   * where they were. The alternative — every query erroring behind a spinner
   * — is indistinguishable from an outage mid-incident.
   */
  useEffect(
    () =>
      api.onSessionExpired(() => {
        queryClient.clear();
        setSignedIn(false);
      }),
    [],
  );

  const signedOut = useCallback(() => {
    void api.signOut();
    queryClient.clear();
    setSignedIn(false);
  }, []);

  // The invitation page (links in invitation emails) works signed in or out:
  // declining needs no account, accepting signs the invitee in in place.
  const invitation = parseInvitationPath(window.location.pathname);
  if (invitation) {
    return (
      <InvitationPage
        token={invitation.token}
        decline={invitation.decline}
        signedIn={signedIn}
        onSignedIn={() => {
          setSignedIn(true);
          void queryClient.invalidateQueries();
        }}
      />
    );
  }

  // The organisation join link (posted in Slack/Teams) works signed in or out
  // too: the page names the organisation, and joining signs the person in in
  // place.
  const joinLink = parseJoinPath(window.location.pathname);
  if (joinLink) {
    return (
      <JoinPage
        token={joinLink.token}
        signedIn={signedIn}
        onSignedIn={() => {
          setSignedIn(true);
          void queryClient.invalidateQueries();
        }}
      />
    );
  }

  if (!signedIn) {
    return (
      <SignIn
        initialError={ssoError}
        onSignedIn={() => {
          setSignedIn(true);
          void queryClient.invalidateQueries();
        }}
      />
    );
  }

  return <Dashboard onSignedOut={signedOut} />;
}

// Guarded so the module can be imported for its pure exports (`parse`,
// `routeSiteFocus`) under test, where there is no `#root` to mount into. In the
// browser the element is always present, so the app mounts exactly as before.
const rootEl = document.getElementById('root');
if (rootEl) {
  // A tab that outlived a deploy asks for chunks the bucket may no longer hold;
  // reload once (guarded) rather than leave the map dead. lib/stale-build.ts.
  installStaleBuildRecovery();
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <StaleBuildNotice />
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>,
  );
}
